/**
 * Subscribe to `/files/watch` for the active session and push refreshed
 * listings into `appState.files`.
 *
 * Fills the gap where `_broadcast_files_update` on the backend no-ops
 * because no SessionState exists yet (i.e. before the first agent run).
 * Folder-sync uploads, docx PUTs, and agent file writes all show up in the
 * panel in real time — not only after an agent run.
 *
 * Session isolation: each sessionId gets its own AbortController; on switch
 * we abort the in-flight list fetch and the SSE stream, and the setState
 * path checks the store's current sessionId before writing so a late response
 * from a prior session can't clobber the new one.
 */

import { useEffect } from "react";
import { listFilesDetailed, subscribeFileWatch } from "../api/files";
import { useChatStoreApi } from "../stores";

export function useFilesWatch(sessionId: string | null) {
  const storeApi = useChatStoreApi();

  useEffect(() => {
    if (!sessionId) return;
    const ac = new AbortController();
    let debounce: number | null = null;

    const refresh = async () => {
      debounce = null;
      let files;
      try {
        files = await listFilesDetailed(sessionId, ac.signal);
      } catch {
        return;
      }
      if (ac.signal.aborted) return;
      storeApi.setState((s) => {
        if (s.activeSessionId !== sessionId) return;
        s.appState = { ...s.appState, files };
      });
    };

    const schedule = () => {
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(refresh, 150);
    };

    refresh();

    const sub = subscribeFileWatch(
      sessionId,
      () => schedule(),
      () => schedule(),
      -1,
    );

    return () => {
      ac.abort();
      if (debounce !== null) window.clearTimeout(debounce);
      sub.close();
    };
  }, [sessionId, storeApi]);
}
